import { LitElement, html, css } from './vendor_lit3-core.min.js';

const SUPPORTED_LOCALES = ['da', 'en', 'sv', 'no'];
const DEFAULT_LOCALE = 'en';
// Shopify uses both 'no' and 'nb' for Norwegian depending on store setup.
const shopLocale = Shopify.locale === 'nb' ? 'no' : Shopify.locale;
const locale = SUPPORTED_LOCALES.includes(shopLocale) ? shopLocale : DEFAULT_LOCALE;

const TRANSLATION_ADD_TO_CALENDAR = {
  da: 'Tilføj til kalender',
  en: 'Add to calendar',
  sv: 'Lägg till kalender',
  no: 'Legg til i kalender',
};
const TRANSLATION_CALENDAR_EVENT_TITLE = {
  da: (productName) => `Release af ${productName} hos Disc Tree`,
  en: (productName) => `Release of ${productName} at Disc Tree`,
  sv: (productName) => `Release av ${productName} hos Disc Tree`,
  no: (productName) => `Release av ${productName} hos Disc Tree`,
};

const TRANSLATIONS_PERIODS = {
  da: {
    singular: ['dag', 'time', 'minut', 'sekund'],
    plural: ['dage', 'timer', 'minutter', 'sekunder'],
  },
  en: {
    singular: ['day', 'hour', 'minute', 'second'],
    plural: ['days', 'hours', 'minutes', 'seconds'],
  },
  sv: {
    singular: ['dag', 'timme', 'minut', 'sekund'],
    plural: ['dagar', 'timmar', 'minuter', 'sekunder'],
  },
  no: {
    singular: ['dag', 'time', 'minutt', 'sekund'],
    plural: ['dager', 'timer', 'minutter', 'sekunder'],
  },
};

class DtCountdownTimerDetail extends LitElement {
  static properties = {
    to: { type: String },
    targetDate: { state: true },
    remainingTime: { state: true },
    completed: { type: Boolean, reflect: true },
    product: { type: String },
  };

  static styles = css`
    .countdown-container {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(80px, max-content));
      gap: 5px;
      justify-content: start;
      align-items: start;
      font-family: Arial, sans-serif;
      margin-block: 10px;
    }

    .box {
      padding-inline: 2px;
      padding-bottom: 2px;
      padding-top: 4px;
      border: 1px solid rgba(var(--color-foreground), 0.75);
      border-radius: 5px;
      text-align: center;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
    }

    .box.hidden {
      display: none;
    }

    .number {
      font-size: 1.8rem;
      font-weight: bold;
      color: rgba(var(--color-foreground), 0.75);
    }

    .label {
      font-size: 1.1rem;
      color: rgba(var(--color-foreground), 0.75);
    }
  `;

  constructor() {
    super();
    this.to = null;
    this.targetDate = null;
    this.remainingTime = null;
    this.completed = false;
  }

  connectedCallback() {
    super.connectedCallback();
    if (this.to) {
      this.targetDate = new Date(this.to);
      this.updateCountdown();

      this.timerId = setInterval(() => {
        this.updateCountdown();
        this.requestUpdate();
      }, 1000);
    }
  }

  disconnectedCallback() {
    if (this.timerId) clearInterval(this.timerId);
    super.disconnectedCallback();
  }

  updateCountdown() {
    const now = new Date();
    this.remainingTime = this.targetDate - now;
    if (this.remainingTime <= 0) {
      if (this.timerId) clearInterval(this.timerId);
      this.dispatchEvent(new CustomEvent('complete'));
      this.completed = true;
    }
  }

  calculateTimeUnits(remainingTime) {
    const days = Math.floor(remainingTime / (1000 * 60 * 60 * 24));
    const hours = Math.floor((remainingTime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((remainingTime % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((remainingTime % (1000 * 60)) / 1000);
    return { days, hours, minutes, seconds };
  }

  getLabel(unit, value) {
    const translationPeriods = TRANSLATIONS_PERIODS[locale];
    return value === 1 ? translationPeriods.singular[unit] : translationPeriods.plural[unit];
  }

  countdownTemplate() {
    if (!this.to || this.remainingTime === null || this.remainingTime <= 0) {
      return html``;
    }

    const { days, hours, minutes, seconds } = this.calculateTimeUnits(this.remainingTime);

    return html`
      <div class="countdown-container">
        <div class="box ${days === 0 ? 'hidden' : ''}">
          <span class="number">${days}</span>
          <span class="label">${this.getLabel(0, days)}</span>
        </div>
        <div class="box ${days === 0 && hours === 0 ? 'hidden' : ''}">
          <span class="number">${hours}</span>
          <span class="label">${this.getLabel(1, hours)}</span>
        </div>
        <div class="box">
          <span class="number">${minutes}</span>
          <span class="label">${this.getLabel(2, minutes)}</span>
        </div>
        <div class="box">
          <span class="number">${seconds}</span>
          <span class="label">${this.getLabel(3, seconds)}</span>
        </div>
      </div>
    `;
  }

  calendarButtonTemplate() {
    const calendarEventAction = 'TEMPLATE';
    const calendarEventDates = `${this.targetDate.toISOString().replace(/[-:]/g, '').slice(0, -5)}Z%2F${this.targetDate
      .toISOString()
      .replace(/[-:]/g, '')
      .slice(0, -5)}Z`;
    const calendarEventText = encodeURIComponent(TRANSLATION_CALENDAR_EVENT_TITLE[locale](this.product));
    const calendarEventLocation = encodeURIComponent(window.location.href);
    const calendarEventDetails = '';
    const calendarEventLink = `https://calendar.google.com/calendar/render?action=${calendarEventAction}&dates=${calendarEventDates}&details=${calendarEventDetails}&location=${calendarEventLocation}&text=${calendarEventText}`;

    return html`
      <sl-button variant="success" href="${calendarEventLink}" target="_blank">
        <sl-icon slot="suffix" name="calendar4-event"></sl-icon>
        ${TRANSLATION_ADD_TO_CALENDAR[locale]}
      </sl-button>
    `;
  }

  formatDateTemplate() {
    return html`
      <sl-format-date
        date="${this.to}"
        year="numeric"
        month="short"
        day="numeric"
        hour="numeric"
        minute="numeric"
        weekday="short"
      ></sl-format-date>
    `;
  }

  render() {
    // If the countdown is completed, display nothing using ternary operator
    return this.completed
      ? html``
      : html`<p>Release: ${this.formatDateTemplate()}</p> ${this.countdownTemplate()} ${this.calendarButtonTemplate()}`;
  }
}

customElements.define('dt-countdown-timer-detail', DtCountdownTimerDetail);
